import * as React from 'react';
import onClickOutside from 'react-onclickoutside';
import classnames from 'classnames';
import findIndex from 'lodash/findIndex';
import moment from 'moment';

import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import Input from '@components/Input';

import { getEmptyErrorState } from '@utils/AppUtil';
import { getAgencyTime } from '@utils/CalendarUtil';
import { getBoundingClientRect, getCurrentWindowSize } from '@utils/WindowUtils';

import './style.scss';

interface IDateFilterOption {
  label: string;
  value: string;
}

export interface IDateFilterProps {
  label: string;
  name: string;
  options: IDateFilterOption[];
  selectedOption: string;
  className?: string;
  key?: any;
  onSubmit: (e: React.SyntheticEvent<any>, handleClose?: boolean) => void;
  isFilterOpen?: boolean;
  showScheduledUnscheduledCheckboxes?: boolean;
  hasScheduledInspection?: boolean;
  hasUnscheduledInspection?: boolean;
  disabled?: boolean;
  onCloseOption?: IDateFilterOption;
}

interface IDateFilterState {
  isFilterOpen: boolean;
  options: IDateFilterOption[];
  selectedIndex: number;
  error: IFieldValidation;
}

export class DateFilter extends React.Component<IDateFilterProps, IDateFilterState> {
  constructor(props: IDateFilterProps) {
    super(props);

    this.state = this.getInitState();
  }

  componentDidUpdate(prevProps: Readonly<IDateFilterProps>) {
    const { selectedOption, options, isFilterOpen } = this.props;

    if (
      prevProps.selectedOption !== selectedOption ||
      prevProps.options !== options ||
      prevProps.isFilterOpen !== isFilterOpen
    ) {
      let selectedIndex = 0;

      options.forEach((elem, index) => {
        if (elem.value === selectedOption) {
          selectedIndex = index;
        }
      });

      if (this.props.hasOwnProperty('isFilterOpen')) {
        this.setState({
          selectedIndex,
          options,
          isFilterOpen,
        });
      } else {
        this.setState({
          selectedIndex,
          options,
        });
      }
    }
  }

  getInitState = () => ({
    isFilterOpen: false,
    options: this.props.options,
    selectedIndex: 0,
    error: getEmptyErrorState(),
  });

  toggleDropdown = (e?: React.SyntheticEvent<any>) => {
    this.setState((prevState) => ({
      isFilterOpen: !prevState.isFilterOpen,
    }));
  };

  onClose = (e?: React.SyntheticEvent<any>) => {
    this.setState({
      isFilterOpen: this.props.isFilterOpen ? this.props.isFilterOpen : false,
      selectedIndex: findIndex(this.props.options, (elem) => elem.value === this.props.selectedOption),
      options: this.props.options,
      error: getEmptyErrorState(),
    });

    if (this.props.onCloseOption) {
      const formattedOption = {
        ...e,
        currentTarget: {
          ...e.currentTarget,
          name: this.props.name,
          value: this.props.onCloseOption.value,
        },
      };

      this.props.onSubmit(formattedOption, true);
    }
  };

  handleClickOutside = () => {
    this.onClose();
  };

  handleChange = (selectedIndex: number) => (e: React.SyntheticEvent<HTMLInputElement>) => {
    this.setState({
      selectedIndex,
    });
  };

  onChange = (index: number, datePickerIndex: number) => (e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const format = 'MM/DD/YYYY';
    const options = this.state.options.map((elem, i) => {
      if (index === i) {
        const selectedValue = elem ? elem.value.split('-') : [];
        selectedValue[`${datePickerIndex}`] = moment(value).format(format);

        return {
          ...elem,
          value: selectedValue.join('-'),
        };
      }

      return elem;
    });

    this.setState({
      options,
    });
  };

  validate = () => {
    const { selectedIndex } = this.state;
    const { options } = this.state;
    const customValue = options[selectedIndex].value;
    const format = 'MM/DD/YYYY';
    const fromDate = moment(customValue.split('-')[0], format);
    const toDate = moment(customValue.split('-')[1], format);
    let isValid = true;

    if (options[selectedIndex].label.toLowerCase() === 'custom' && toDate.isBefore(fromDate)) {
      this.setState({
        error: {
          errorState: IValidationState.ERROR,
          errorMessage: 'End Date is before Start Date.',
        },
      });

      isValid = false;
    }

    return isValid;
  };

  handleOnSubmit = (e: React.SyntheticEvent<MouseEvent>) => {
    if (this.validate()) {
      const selectedOption = this.state.options[this.state.selectedIndex];
      const formattedOption = {
        ...e,
        currentTarget: {
          ...e.currentTarget,
          name: this.props.name,
          value: selectedOption.value,
        },
      };

      this.onSubmit(formattedOption);
    }
  };

  onSubmit = (formattedOption: any) => {
    this.props.onSubmit(formattedOption);

    this.setState({
      isFilterOpen: false,
      error: getEmptyErrorState(),
    });
  };

  handleScheduledChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const formattedOption = {
      ...e,
      currentTarget: {
        ...e.currentTarget,
        name: 'hasScheduledInspection',
        value: !this.props.hasScheduledInspection,
      },
    };

    this.onSubmit(formattedOption);
  };

  handleUnscheduledChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const formattedOption = {
      ...e,
      currentTarget: {
        ...e.currentTarget,
        name: 'hasUnscheduledInspection',
        value: !this.props.hasUnscheduledInspection,
      },
    };

    this.onSubmit(formattedOption);
  };

  getDate = (value: string, type: string, format: string = 'MM/DD/YYYY') => {
    const now = getAgencyTime();
    const time = {
      hour: now.get('hour') + Math.floor(now.utcOffset() / -60) + Math.floor(new Date().getTimezoneOffset() / -60),
      minute: now.get('minute') + (now.utcOffset() % 60) * -1 + (new Date().getTimezoneOffset() % 60) * -1,
      second: now.get('second'),
    };

    if (type.toLowerCase() === 'from') {
      return moment(value.split('-')[0], format).set(time);
    }

    return moment(value.split('-')[1], format).set(time);
  };

  renderDropdown = () => {
    const { options } = this.state;
    const windowSize = getCurrentWindowSize();
    const boundingClientRect = getBoundingClientRect(`#${this.props.name}`);
    const style = boundingClientRect
      ? {
          ...(windowSize > boundingClientRect.left + 400 ? {} : { right: 0 }),
        }
      : null;

    const { showScheduledUnscheduledCheckboxes, hasScheduledInspection, hasUnscheduledInspection, disabled } =
      this.props;

    return (
      <div className={classnames('custom-filter__dropdown')} style={style}>
        {showScheduledUnscheduledCheckboxes && (
          <Checkbox
            id="scheduledInspection"
            name="scheduledInspection"
            children="Next Scheduled Inspections"
            isChecked={hasScheduledInspection}
            onChange={this.handleScheduledChange}
            className="checkbox-scheduled"
          />
        )}
        <ul className={classnames('custom-filter__dropdown__options')}>
          {options.map((option, index) => (
            <li key={index}>
              <Checkbox
                id={`${this.props.label}-${index}`}
                onChange={this.handleChange(index)}
                isChecked={index === this.state.selectedIndex}
                name={option.label}
                className="checkbox-holder--radio"
                disabled={disabled}
              >
                {option.label}
              </Checkbox>
              {option.label.toLowerCase() === 'custom' && (
                <div className={classnames('custom-filter__dropdown__custom')}>
                  <Input
                    field={{
                      label: '',
                      type: CustomFieldType.DATE,
                      isRequired: false,
                      value: this.getDate(option.value, 'from'),
                    }}
                    width={12}
                    name="fromDate"
                    onChange={this.onChange(index, 0)}
                    onFocus={this.handleChange(index)}
                    disabled={disabled}
                  />
                  <span className={classnames('space-right', 'space-left')}>-</span>
                  <Input
                    field={{
                      label: '',
                      type: CustomFieldType.DATE,
                      isRequired: false,
                      value: this.getDate(option.value, 'to'),
                    }}
                    error={this.state.error}
                    width={12}
                    name="toDate"
                    onChange={this.onChange(index, 1)}
                    onFocus={this.handleChange(index)}
                    disabled={disabled}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
        <div className={classnames('flex-row--center', 'custom-filter__dropdown__actions')}>
          <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onClose} content="Cancel" disabled={disabled} />
          <Button
            className={classnames('space-left')}
            bsStyle={ButtonStyle.PRIMARY}
            onClick={this.handleOnSubmit}
            content="Apply"
            disabled={disabled}
          />
        </div>
        {showScheduledUnscheduledCheckboxes && (
          <Checkbox
            id="unscheduledInspection"
            name="unscheduledInspection"
            children="Unscheduled Inspections"
            isChecked={hasUnscheduledInspection}
            onChange={this.handleUnscheduledChange}
            className="checkbox-unscheduled"
          />
        )}
      </div>
    );
  };

  renderSelector = () => {
    const {
      options,
      selectedOption,
      showScheduledUnscheduledCheckboxes,
      hasScheduledInspection,
      hasUnscheduledInspection,
    } = this.props;
    const option = options.find((elem) => elem.value === selectedOption);
    let filterLabel = option ? (option.label.toLowerCase() === 'custom' ? option.value : option.label) : '';

    if (showScheduledUnscheduledCheckboxes) {
      if (hasScheduledInspection && hasUnscheduledInspection) {
        filterLabel = `${filterLabel}, Unscheduled`;
      } else if (!hasScheduledInspection && hasUnscheduledInspection) {
        filterLabel = 'Unscheduled';
      }
    }

    return (
      <div
        className={classnames('custom-filter__selector')}
        onClick={(e) => !this.props.hasOwnProperty('isFilterOpen') && this.toggleDropdown(e)}
      >
        <label className={classnames('custom-filter__selector__label', 'light-text')}>{this.props.label}</label>
        <div className={classnames('custom-filter__selector__selected')}>
          <label>{filterLabel}</label>
          {!this.props.hasOwnProperty('isFilterOpen') && <span className={classnames('custom-filter__arrow')} />}
        </div>
      </div>
    );
  };

  render() {
    return (
      <div
        id={this.props.name}
        key={this.props.key || 'unique'}
        className={classnames('custom-filter', this.props.className || '', {
          'custom-filter--open': this.state.isFilterOpen,
        })}
      >
        {this.renderSelector()}
        {this.renderDropdown()}
      </div>
    );
  }
}

export default onClickOutside(DateFilter);
